import type { CSSProperties } from "react";
import { MAX_LOADED_DRINKS } from "../state/sessionStore";

export interface LoadedTrayDrink {
  slug: string;
  name: string;
  cocktailCount: number;
}

interface Props {
  drinks: LoadedTrayDrink[];
  activeSlug: string | null;
  onSelect: (slug: string) => void;
  onRemove: (slug: string) => void;
  onLoadMore: () => void;
}

export default function LoadedTray({ drinks, activeSlug, onSelect, onRemove, onLoadMore }: Props) {
  return (
    <section style={styles.tray} aria-label="Loaded drinks">
      <div style={styles.title}>
        <span>Loaded</span>
        <span>{drinks.length}/{MAX_LOADED_DRINKS}</span>
      </div>
      <div style={styles.grid}>
        {drinks.map((d) => {
          const active = d.slug === activeSlug;
          return (
            <div key={d.slug} style={styles.slotWrap}>
              <button
                type="button"
                onClick={() => onSelect(d.slug)}
                style={{
                  ...styles.slot,
                  background: active ? "var(--text)" : "var(--surface)",
                  color: active ? "var(--bg)" : "var(--text)",
                  borderColor: active ? "var(--text)" : "var(--border)",
                }}
                aria-pressed={active}
                aria-label={`Select ${d.name}, ${d.cocktailCount} ${d.cocktailCount === 1 ? "cocktail" : "cocktails"}`}
              >
                <div style={styles.slotName}>{d.name}</div>
                <div
                  style={{
                    ...styles.slotCount,
                    color: active ? "var(--bg)" : "var(--muted)",
                  }}
                >
                  {d.cocktailCount}× cocktail{d.cocktailCount === 1 ? "" : "s"}
                </div>
              </button>
              <button
                type="button"
                onClick={() => onRemove(d.slug)}
                style={{
                  ...styles.close,
                  color: active ? "var(--bg)" : "var(--muted)",
                }}
                aria-label={`Remove ${d.name}`}
              >
                ✕
              </button>
            </div>
          );
        })}
        {drinks.length < MAX_LOADED_DRINKS && (
          <button
            type="button"
            onClick={onLoadMore}
            style={{ ...styles.slot, ...styles.empty }}
          >
            <div style={{ ...styles.slotName, color: "var(--muted)" }}>+ Load drink</div>
          </button>
        )}
      </div>
    </section>
  );
}

const styles: Record<string, CSSProperties> = {
  tray: {
    background: "var(--surface)",
    border: "1px solid var(--border)",
    borderRadius: 14,
    padding: 10,
    marginTop: 8,
  },
  title: {
    display: "flex",
    justifyContent: "space-between",
    fontSize: 10,
    fontWeight: 800,
    letterSpacing: 0.6,
    textTransform: "uppercase",
    color: "var(--muted)",
    marginBottom: 6,
  },
  grid: {
    display: "grid",
    gridTemplateColumns: `repeat(${MAX_LOADED_DRINKS}, 1fr)`,
    gap: 6,
  },
  slotWrap: { position: "relative" },
  slot: {
    width: "100%",
    border: "1px solid",
    borderRadius: 11,
    padding: "10px 36px 10px 8px",
    minHeight: 56,
    cursor: "pointer",
    textAlign: "left",
    fontSize: 12,
    fontWeight: 800,
    lineHeight: 1.15,
    overflow: "hidden",
  },
  empty: {
    background: "var(--bg)",
    color: "var(--muted)",
    borderStyle: "dashed",
  },
  slotName: {
    paddingRight: 18,
    fontSize: 12,
    fontWeight: 800,
    lineHeight: 1.15,
    overflow: "hidden",
    textOverflow: "ellipsis",
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
  },
  slotCount: {
    fontSize: 10,
    fontWeight: 700,
    marginTop: 4,
    opacity: 0.85,
  },
  close: {
    position: "absolute",
    top: 4,
    right: 4,
    width: 32,
    height: 32,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 13,
    fontWeight: 900,
    cursor: "pointer",
    background: "transparent",
    border: 0,
    borderRadius: 8,
    lineHeight: 1,
    userSelect: "none",
  },
};
